import { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import TutorApplicationForm from "../components/TutorApplicationForm";
import SEO from "../components/SEO";
import BookMyTutorPromo from "../components/BookMyTutorPromo";
import TutorCommitments from "../components/TutorCommitments";
import { QuickAnswerCard, DefinitionBox, ConversationalFAQ, buildConversationalFAQSchema } from "../components/AEOContent";
import { BECOME_A_TUTOR_AEO } from "../data/aeoContent";
import {
    GraduationCap, IndianRupee, Clock, MapPin, HelpCircle, ArrowRight, BookOpen,
    ShieldCheck, Sparkles, Download, QrCode,
} from "lucide-react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_EXTERNAL_URL = "https://www.bookmytutor.com/download";
const BOOKMYTUTOR_LOGO_URL = "/bookmytutor-logo.png";

/**
 * V36 — When the admin sets `tutor_registration_mode = "external_app"`, we
 * keep the entire Become-a-Tutor page intact (hero, benefits, commitments,
 * promo, FAQs) and ONLY swap the registration form section with a prominent
 * Book My Tutor App CTA card. When the admin toggles back to "website" the
 * original form re-appears in the exact same slot — no redirects, no layout
 * change, no SEO/URL change. The switch is live (no deploy required).
 */
function BookMyTutorAppCta({ url }) {
    const target = url || DEFAULT_EXTERNAL_URL;
    return (
        <div
            className="relative overflow-hidden bg-gradient-to-br from-[#E03A2F] via-[#E84A1B] to-[#F35F1A] text-white rounded-2xl shadow-xl p-8 sm:p-10 flex flex-col items-center text-center"
            data-testid="become-tutor-bookmytutor-cta-card"
        >
            <div className="absolute -top-12 -right-12 w-40 h-40 bg-white/10 rounded-full" />
            <div className="absolute -bottom-16 -left-10 w-48 h-48 bg-white/10 rounded-full" />

            {/* App logo — <picture> element with AVIF → WebP → PNG fallback.
                Local (same-origin) assets so it always paints on mobile — no
                CDN latency, no fetch-timeout blank box. */}
            <div
                className="relative w-60 sm:w-72 h-24 rounded-2xl bg-white shadow-lg flex items-center justify-center overflow-hidden mb-5"
                data-testid="become-tutor-bookmytutor-icon"
            >
                <picture>
                    <source srcSet="/bookmytutor-logo.avif" type="image/avif" />
                    <source srcSet="/bookmytutor-logo.webp" type="image/webp" />
                    <img
                        src={BOOKMYTUTOR_LOGO_URL}
                        alt="Book My Tutor — Tutor App"
                        width="480"
                        height="480"
                        fetchPriority="high"
                        decoding="async"
                        className="w-full h-full object-contain scale-90 sm:scale-100"
                    />
                </picture>
            </div>

            <h3
                className="relative font-heading text-2xl sm:text-3xl font-bold leading-tight mb-2"
                data-testid="become-tutor-bookmytutor-heading"
            >
                Become a Tutor with Anand Tutorials
            </h3>
            <p
                className="relative text-sm sm:text-base text-white/95 max-w-md mb-7"
                data-testid="become-tutor-bookmytutor-description"
            >
                Register through the Book My Tutor App to join our tutor network.
            </p>

            <a
                href={target}
                target="_blank"
                rel="noopener noreferrer"
                className="relative inline-flex items-center justify-center gap-2 bg-white text-[#E03A2F] font-bold text-base sm:text-lg px-7 py-3.5 rounded-full shadow-lg hover:scale-[1.02] active:scale-[0.98] transition w-full sm:w-auto"
                data-testid="become-tutor-bookmytutor-cta-button"
            >
                <Download className="w-5 h-5" />
                Download Book My Tutor App
            </a>

            {/* QR code panel — desktop scan-to-install */}
            <div
                className="relative mt-6 bg-white rounded-2xl shadow-lg px-5 py-4 flex items-center gap-4 max-w-xs"
                data-testid="become-tutor-bookmytutor-qr-panel"
            >
                <div className="bg-white rounded-lg p-1.5 ring-1 ring-slate-200 shrink-0">
                    <QRCodeSVG
                        value={target}
                        size={96}
                        bgColor="#FFFFFF"
                        fgColor="#0F172A"
                        level="M"
                        includeMargin={false}
                        data-testid="become-tutor-bookmytutor-qr-svg"
                    />
                </div>
                <div className="text-left">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[#E03A2F]">
                        <QrCode className="w-3 h-3" /> Scan to install
                    </div>
                    <p className="text-[12px] text-slate-700 leading-snug mt-1 font-medium">
                        Point your phone camera at the code to open the Book My Tutor App download.
                    </p>
                </div>
            </div>

            {/* Reassurance row */}
            <div className="relative mt-6 grid grid-cols-3 gap-3 w-full max-w-md text-[11px] sm:text-xs text-white/95">
                <div className="flex flex-col items-center gap-1">
                    <ShieldCheck className="w-4 h-4" />
                    <span>Verified Platform</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                    <IndianRupee className="w-4 h-4" />
                    <span>Industry-Leading Pay</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                    <Sparkles className="w-4 h-4" />
                    <span>1-Tap Apply</span>
                </div>
            </div>
        </div>
    );
}

export default function BecomeATutor() {
    // V36 — Default to "website" so the form renders without waiting on the
    // settings call. If the admin has switched to external_app, we swap the
    // form for the CTA card as soon as the public settings endpoint responds.
    const [mode, setMode] = useState("website");
    const [externalUrl, setExternalUrl] = useState(DEFAULT_EXTERNAL_URL);

    useEffect(() => {
        let cancelled = false;
        axios.get(`${BACKEND_URL}/api/settings/public`)
            .then((r) => {
                if (cancelled) return;
                const m = (r.data?.tutor_registration_mode || "website").toLowerCase();
                const u = r.data?.tutor_registration_external_url || DEFAULT_EXTERNAL_URL;
                setExternalUrl(u);
                setMode(m === "external_app" ? "external_app" : "website");
            })
            .catch(() => { /* fall back to website mode */ });
        return () => { cancelled = true; };
    }, []);

    return (
        <div data-testid="become-tutor-page">
            <SEO
                title="Become a Tutor with Anand Tutorials — Apply to Teach 1:1"
                description="Apply to teach 1-on-1 home or online with Anand Tutorials. Industry-leading pay, flexible hours, free training. CBSE, ICSE, IGCSE, IB, NEET, JEE tutors needed."
                keywords="tutor jobs Jabalpur, tutor jobs Bhopal, tutor jobs Nagpur, online tutor jobs India, become a tutor, teach online, home tutor jobs"
                path="/become-a-tutor"
                schemas={[buildConversationalFAQSchema(BECOME_A_TUTOR_AEO.faqs)]}
            />
            <PageHeader
                eyebrow="Join Our Tutor Network"
                title="Teach with Anand Tutorials."
                subtitle="Join 2,500+ verified educators delivering 1-on-1 home & online tuition across India and worldwide. Flexible hours, fair pay, real impact."
            />

            {/* V65 — AEO Quick-Answer card */}
            <QuickAnswerCard
                question={BECOME_A_TUTOR_AEO.quickAnswer.question}
                answer={BECOME_A_TUTOR_AEO.quickAnswer.answer}
                bullets={BECOME_A_TUTOR_AEO.quickAnswer.bullets}
                footer={BECOME_A_TUTOR_AEO.quickAnswer.footer}
                testId="become-tutor-quick-answer"
            />

            {/* Tutor Guide callout — first-time visitors + returning tutors */}
            <section className="bg-gradient-to-r from-blue-50 via-white to-amber-50 border-b border-slate-200" data-testid="become-tutor-guide-callout">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0" data-testid="become-tutor-guide-icon">
                            <BookOpen className="w-5 h-5" aria-hidden="true" />
                        </div>
                        <div className="min-w-0">
                            <div className="font-heading font-semibold text-slate-900 text-sm">New here? Read the Tutor Guide first.</div>
                            <div className="text-xs text-slate-600">Complete onboarding: profile → assignments → demo → attendance → payment.</div>
                        </div>
                    </div>
                    <Link to="/tutor-guide" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold shadow shrink-0" data-testid="become-tutor-guide-cta">
                        Open Tutor Guide <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </section>

            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
                    <div>
                        <h2 className="font-heading text-3xl font-bold mb-6">Why teach with us?</h2>
                        <div className="space-y-4">
                            {[
                                [IndianRupee, "Industry-leading pay", "Top-tier per-session rates with on-time monthly payouts."],
                                [Clock, "Flexible hours", "Choose your slots — morning, evening, weekends only."],
                                [MapPin, "Home or online", "Take students near you, or teach 1:1 online from anywhere."],
                                [GraduationCap, "Career growth", "Get matched with international students and senior-class learners."],
                            ].map(([I, t, d]) => (
                                <div key={t} className="flex gap-3">
                                    <div className="shrink-0 w-12 h-12 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center"><I className="w-5 h-5" /></div>
                                    <div>
                                        <div className="font-heading font-semibold">{t}</div>
                                        <div className="text-sm text-slate-600">{d}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div data-testid="become-tutor-registration-slot">
                        {mode === "external_app" ? (
                            <BookMyTutorAppCta url={externalUrl} />
                        ) : (
                            <TutorApplicationForm />
                        )}
                    </div>
                </div>
            </section>
            <TutorCommitments />
            <BookMyTutorPromo />

            {/* V65 — AEO Definition block */}
            <DefinitionBox
                term={BECOME_A_TUTOR_AEO.definition.term}
                definition={BECOME_A_TUTOR_AEO.definition.definition}
                examples={BECOME_A_TUTOR_AEO.definition.examples}
                testId="become-tutor-definition"
            />

            {/* V65 — AEO Conversational FAQ (long-form, voice-search + LLM ingestion) */}
            <ConversationalFAQ
                heading="Deeper questions from tutors evaluating our panel"
                subheading="Long-form answers to the questions we most often hear from applicants — eligibility, selection, payment, growth."
                items={BECOME_A_TUTOR_AEO.faqs}
                testId="become-tutor-conversational-faq"
            />

            {/* FAQ hub teaser */}
            <section className="py-12 bg-[#F8FAFC] border-t border-slate-100">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-[#1E3A8A] text-xs font-semibold tracking-wide uppercase mb-3">
                        <HelpCircle className="w-3.5 h-3.5" /> 88 Questions Answered
                    </div>
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-3">Still have questions?</h2>
                    <p className="text-slate-600 mb-5">
                        Read our complete 88-FAQ Become-a-Tutor guide — eligibility, registration (no joining fee), profile verification, subject interview, earnings, demo classes, assignment flow, female tutor opportunities, app usage and more.
                    </p>
                    <Link
                        to="/become-a-tutor-faqs"
                        data-testid="become-tutor-faqs-link"
                        className="inline-flex items-center gap-2 bg-[#1E3A8A] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#1d3478]"
                    >
                        Read Tutor FAQs <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
